import React, { FC, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import {
    getTextAreaMaxHeightCss,
    getTextAreaMaxHeightPx,
    getTextAreaMinHeightCss,
    nextAutoGrowHeight,
    resolveTextFieldSize,
    TextFieldSize
} from '../../../tools/textAreaSizing';
import { EHintType, Hint } from '../Hint/Hint';
import { LabelWithEllipsis } from '../Label/Label';

const Wrapper = styled.div<{ error: boolean, disabled: boolean, $minHeight: string, $maxHeight: string }>`
    textarea {
        overflow: hidden;
        resize: none;
        border: 2px solid ${colorStack.ligthGrey};
        background: ${colorStack.whiteGrey};
        padding: 1rem;
        border-radius: 8px;
        width: 100%;
        min-height: ${props => props.$minHeight};
        max-height: ${props => props.$maxHeight};
        outline: none;
        color: ${colorStack.content};
        ${fontSizeAndHeight[16]};
        &::placeholder {
            color: ${colorStack.middleGrey};
            font-weight: 400;
            ${fontSizeAndHeight[16]};
            font-family: 'Roboto', sans-serif;
            &:disabled {
                color: ${colorStack.middleGrey};
            }
        }
        &:hover {
            border: 2px solid ${colorStack.middleBlue};
        };
        &:focus {
            border: 2px solid ${colorStack.middleBlue};
        };
        &:focus-visible {
            border: 2px solid ${colorStack.middleBlue};
        };
        &:active {
            border: 2px solid ${colorStack.middleBlue};
        };
        ${({ disabled }) => disabled && css`
            border: 2px solid ${colorStack.disabled};
            background: ${colorStack.ligthGrey};
        `}
        ${({ error }) => error && css`
            border: 2px solid ${colorStack.middleRed};
            &:hover {
                border: 2px solid ${colorStack.middleRed};
            };
            &:focus {
                border: 2px solid ${colorStack.middleRed};
            };
            &:focus-visible {
                border: 2px solid ${colorStack.middleRed};
            };
            &:active {
                border: 2px solid ${colorStack.middleRed};
            };
        `};
    }
`;

interface IProps {
    value: string;
    placeholder?: string;
    disabled?: boolean;
    errorMsg?: string;
    label?: string | React.ReactNode;
    tooltipMsg?: string;
    tooltipTitle?: string;
    required?: boolean;
    size?: TextFieldSize;
    onChange(e: React.ChangeEvent<HTMLTextAreaElement>)
}

export const TextArea: FC<React.PropsWithChildren<IProps>> = props => {
    const ref = useRef<HTMLTextAreaElement>(undefined);
    const size = resolveTextFieldSize(props.size);
    const minHeightCss = getTextAreaMinHeightCss(size);
    const maxHeightCss = getTextAreaMaxHeightCss(size);

    useEffect(() => {
        adjustHeight();
    }, [props.value, size]);

    const adjustHeight = () => {
        const textarea = ref.current;
        if (!textarea) {
            return;
        }
        const maxHeight = getTextAreaMaxHeightPx(size);
        textarea.style.maxHeight = 'none';
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const nextHeight = nextAutoGrowHeight(scrollHeight, 0, maxHeight);
        textarea.style.maxHeight = '';
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        props.onChange(e);
    };

    return (
        <Wrapper error={!!props.errorMsg} disabled={props.disabled} $minHeight={minHeightCss} $maxHeight={maxHeightCss}>
            {props.label && <LabelWithEllipsis required={props.required} tooltipMsg={props.tooltipMsg} tooltipTitle={props.tooltipTitle}>{props.label}</LabelWithEllipsis>}
            <textarea ref={ref} onChange={handleChange} value={props.value} disabled={props.disabled} placeholder={props.placeholder} />
            {props.errorMsg &&
                <Hint type={EHintType.ERROR}>{props.errorMsg}</Hint>
            }
        </Wrapper>
    );
};